import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';

import { CDNAssets } from '../../CDN';
import AlternateLayoutList from '../../components/layout/lists/AlternateLayoutList';
import HeaderSection, { HeaderSectionDestinationUrl } from '../../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../../components/layout/sections/PageSection';
import PageSectionTitle from '../../components/layout/sections/PageSectionTitle';
import { StrapiApiClient } from '../../lib/integrations/strapi/StrapiApiClient';
import { StrapiApiPlugin } from '../../lib/integrations/strapi/types';
import { withGlobalDefaultStaticProps } from '../../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../../lib/seo/pageSeoConfigs';
import pageRoutes from '../../router/pageRoutes';
import { NextPageWithLayout } from '../../types/global-types';
import MetricsCards from '../../widgets/cards/MetricsCards';
import FAQWidget from '../../widgets/interactive/FAQWidget';
import AllIntegrationsShowcase from '../../widgets/showcases/AllIntegrationsShowcase';
import { FeatureShowcaseCarouselOverlay } from '../../widgets/showcases/FeatureShowcase/FeatureShowcase';
import PlatformShowcase, { SegmentOption } from '../../widgets/showcases/PlatformShowcase';

type GasparForITPageStaticProps = {
  plugins: StrapiApiPlugin[];
};

export const getStaticProps: GetStaticProps<GasparForITPageStaticProps> = async (context) => {
  const plugins = await StrapiApiClient.getAllPlugins();
  const props: GasparForITPageStaticProps = { plugins };
  return withGlobalDefaultStaticProps(context, { props });
};

const GasparForITPage: NextPageWithLayout<GasparForITPageStaticProps> = (props) => {
  const { plugins } = props;
  const seoConfig = getPageSeoConfig(pageRoutes.GasparForIT);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        subTitle="Gaspar for IT"
        title="AI helpdesk for smarter IT support"
        description="Improve your IT operations' efficiency and supercharge employee productivity with our cutting-edge AI helpdesk. Offer automated IT support solutions and free up your helpdesk."
        destinationUrl={HeaderSectionDestinationUrl.RequestDemo}
      />
      <PageSection isNarrowTop={true}>
        <MetricsCards
          records={[
            {
              metricValue: '40%',
              title: 'Instant automated IT support',
              description:
                'Give your employees answers and solutions instantly in one place with no human intervention. We go beyond providing answers, ensuring friendly, human-like interactions that enhance user experience. Employees will be more satisfied, and your IT helpdesk team will have time to engage with strategic, impactful projects.',
            },
            {
              metricValue: '96%',
              title: 'Reduction in the ticket cost',
              description:
                'Achieve a 96% reduction in costs by automating repetitive tasks. Our AI helpdesk streamlines processes, ensuring efficient resource utilization and significant financial savings. Our customers enjoy a helpdesk ticket cost as low as $1.',
            },
            {
              metricValue: '100x',
              title: 'Faster resolution time',
              description:
                'Resolve issues 100x faster: routine issues are resolved in seconds, allowing employees to return to work and freeing up your IT helpdesk to focus on the complex ones and solve them promptly. As a result, employees and your helpdesk team alike enjoy higher productivity.',
            },
          ]}
        />
      </PageSection>
      <PageSection type={PageSectionType.FeatureShowcaseOverlay}>
        <FeatureShowcaseCarouselOverlay
          title="IT helpdesk automation showcase"
          description="Dive into a showcase of our top automations in action"
          panels={[
            { imageUrl: CDNAssets.getUrl('for-it-page-password-auto-reset'), label: 'Password auto-reset' },
            { imageUrl: CDNAssets.getUrl('for-it-page-group-management'), label: 'Group management' },
            { imageUrl: CDNAssets.getUrl('for-it-page-email-list-management'), label: 'Email list management' },
            { imageUrl: CDNAssets.getUrl('for-it-page-hardware-provisioning'), label: 'Hardware provisioning' },
            { imageUrl: CDNAssets.getUrl('for-it-page-software-provisioning'), label: 'Software provisioning' },
            { imageUrl: CDNAssets.getUrl('for-it-page-folder-access-approvals'), label: 'Folder access approvals' },
          ]}
        />
      </PageSection>
      <PageSection type={PageSectionType.White}>
        <PageSectionTitle text="Empower your IT support team with AI" />
        <AlternateLayoutList
          items={[
            {
              imageUrl: CDNAssets.getUrl('for-it-page-smart-automation'),
              orientation: 'left',
              title: 'Smart automation',
              description:
                'Automate repetitive tasks such as password resets, group additions, email list removals, software access and laptop provisioning with Generative AI.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-it-page-quick-approvals'),
              orientation: 'right',
              title: 'Quick approvals',
              description:
                'Speed up decision-making from days to minutes with a streamlined approval process on one platform. Instantly give access to folders, groups, distribution lists.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-it-page-instant-answers'),
              orientation: 'left',
              title: 'Instant answers',
              description:
                'Empower your workforce to instantly find the information they are looking for: IT troubleshooting, how-to questions, privacy policies. All just a question away from our GenAI Virtual Agent!',
            },
            {
              imageUrl: CDNAssets.getUrl('for-it-page-automated-workflows'),
              orientation: 'right',
              title: 'Automated workflows',
              description:
                'Use our no-code templates to craft custom workflows that automate routine, manual processes. Create accounts in seconds and simplify onboarding and offboarding.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-it-page-ticketing-made-easy'),
              orientation: 'left',
              title: 'Ticketing made easy',
              description:
                'Effortlessly create and manage tickets directly within Slack and MS Teams. Information is automatically synced with your ITSM, while users and your IT helpdesk get status updates on the chat platforms.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-it-page-enchanced-knowledge-management'),
              orientation: 'right',
              title: 'Enhanced knowledge management',
              description:
                'Better utilize your existing knowledge by making it immediately available to employees. You can also identify article gaps with AI recommendations based on real employee demand and create relevant content.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-it-page-impactful-business-insights'),
              orientation: 'left',
              title: 'Impactful business insights',
              description:
                'Discover high-impact automation use cases for your IT helpdesk team. Uncover what’s hurting employee productivity and identify which automation opportunities, process improvements and knowledge content will have the biggest business impact.',
            },
            {
              imageUrl: CDNAssets.getUrl('for-it-page-clear-performance-metrics'),
              orientation: 'right',
              title: 'Clear performance metrics',
              description:
                'Measure your investment’s impact across the enterprise and your IT helpdesk. Monitor which features, capabilities, and resources employees find valuable. Compare performance over time and take data-driven action.',
            },
          ]}
        />
      </PageSection>
      <PageSection type={PageSectionType.BlueElectric}>
        <PlatformShowcase
          images={{
            [SegmentOption.Slack]: CDNAssets.getUrl('for-it-page-slack'),
            [SegmentOption.Teams]: CDNAssets.getUrl('for-it-page-teams'),
            [SegmentOption.JIRA]: CDNAssets.getUrl('for-it-page-jira'),
          }}
        />
      </PageSection>
      <PageSection>
        <AllIntegrationsShowcase plugins={plugins} />
      </PageSection>
      <PageSection isNarrowTop={true}>
        <FAQWidget />
      </PageSection>
    </>
  );
};
GasparForITPage.displayName = 'GasparForITPage';
export default GasparForITPage;
